import React from 'react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useToast } from '@quality24/design-system';
import { UUIDv4 } from '@quality24/inpatient-typings';

import useTriggerOrder, {
  HookResult,
} from '@/inpatient-patient-pwa/hooks/useTriggerOrder';
import { CheckAdmissionCoverageResult } from '@/inpatient-patient-pwa/pages/OrderTriggerPage/types';
import { FETCH_ADMISSION_COVERAGE } from './graphql';

export interface UseOrderFlowOptions {
  admissionId?: UUIDv4;
  /** Called once after a successful order creation. Navigation is the caller's job. */
  onTriggered?: () => void;
}

export interface UseOrderFlowResult
  extends Pick<
    HookResult,
    'currentStep' | 'reason' | 'serviceCustomFields' | 'dispatch' | 'operation'
  > {
  shouldShowCustomFields: boolean;
}

/**
 * Encapsulates the order trigger state machine plus the coverage check and the
 * post-success callback, so the modal orchestration can be shared between the
 * HomePage `OrderButton` and the `OrderTriggerPage` listing without duplication.
 */
export const useOrderFlow = ({
  admissionId,
  onTriggered,
}: UseOrderFlowOptions): UseOrderFlowResult => {
  const { error: errorToast } = useToast();
  const { t } = useTranslation();

  const { currentStep, reason, serviceCustomFields, dispatch, operation } =
    useTriggerOrder(admissionId);

  const [checkCoverage] = useLazyQuery<CheckAdmissionCoverageResult>(
    FETCH_ADMISSION_COVERAGE,
  );

  // Determine if we should show custom fields step
  const shouldShowCustomFields =
    serviceCustomFields.length > 0 &&
    !!reason &&
    currentStep === 'custom-fields-form' &&
    !reason.hasChildren;

  // Notify caller on success (no automatic navigation)
  React.useEffect(() => {
    if (operation.called && !operation.loading && !operation.error) {
      onTriggered?.();
    }
  }, [operation, onTriggered]);

  // Perform coverage check if state === 'check-coverage'
  React.useEffect(() => {
    if (currentStep === 'check-coverage') {
      if (reason?.paymentConfig?.data.validateCoverage) {
        checkCoverage({
          variables: {
            admissionId,
            type: reason?.paymentConfig?.data.coverageType || 'MEAL',
          },
        })
          .then(({ data: queryData, error: queryError }) => {
            if (queryError) {
              throw queryError;
            }

            dispatch({
              type: 'check-coverage',
              covered: queryData?.checkAdmissionCoverage.status === 'COVERED',
            });
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Coverage check failed:', err);
            errorToast(t('errors.coverageCheckFailed'));
            dispatch({ type: 'reset', admissionId });
          });
      } else {
        dispatch({
          type: 'check-coverage',
          covered: false,
        });
      }
    }
  }, [
    admissionId,
    checkCoverage,
    currentStep,
    dispatch,
    errorToast,
    reason?.paymentConfig?.data.coverageType,
    reason?.paymentConfig?.data.validateCoverage,
    t,
  ]);

  return {
    currentStep,
    reason,
    serviceCustomFields,
    dispatch,
    operation,
    shouldShowCustomFields,
  };
};

export default useOrderFlow;
